import React, { useState } from 'react'
import { Box, FormHelperText } from '@mui/material'
import { Images } from '@/assets/configs'
import FileDropZone from '@/components/EMT/FileDropZone'
import { useMobile } from 'emt-components-v2'

const gap = 12

type FileType = string | ArrayBuffer | File | null

interface ImageUploaderProps {
  images: Array<FileType>
  avatar: FileType
  errors: Record<string, string>
  onImagesChange: (images: Array<FileType>, index?: number) => void
  onAvatarChange: (avatar: FileType) => void
  color?: string
  variant?: 'signup' | 'base'
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  images,
  avatar,
  errors,
  onImagesChange,
  onAvatarChange,
  color = '#AE59C6',
  variant = 'base',
}) => {
  const [isMobile] = useMobile()
  const [currentImageView, setCurrentImageView] = useState<FileType>(null)

  const handleDrop = (index: number) => (file: FileType) => {
    const newImages = [...images]
    newImages[index] = file
    onImagesChange(
      newImages.filter((img): img is NonNullable<typeof img> => img !== null),
      index,
    )
    setCurrentImageView(file)
  }

  const handleAvatarDrop = (file: FileType) => {
    onAvatarChange(file)
    setCurrentImageView(file)
  }

  return (
    <Box sx={{ marginBottom: errors.avatar ? '0' : '20px' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '2fr 1fr' : '2fr 3fr',
          maxWidth: '100%',
          gap: `${gap}px`,
        }}
      >
        <Box sx={{ '&>div, &>div>div': { width: '100%', height: '100%' } }}>
          <FileDropZone
            overrideIllustration={color === 'primary' ? Images.FileDropZone.Owner : Images.FileDropZone.OwnerPetsitter}
            variant="default"
            isOwner={true}
            preview={avatar as any}
            maxFiles={1}
            multipleFiles={false}
            onDrop={handleAvatarDrop}
            overrideDropZoneBox={{
              width: isMobile ? '100%' : '100%',
              height: isMobile ? 'auto' : '100%',
              // aspectRatio: 0.9,
              border: (theme: any) =>
                avatar === currentImageView && avatar !== null
                  ? `2px solid ${(theme.palette as any).purple.dark}`
                  : null,
              borderColor: errors.avatar ? 'red' : '#BBBABF',
              transition: 'none',
              '& .emt-icons': {
                top: 'unset',
                color: (theme: any) => (theme.palette as any).purple.dark,
                fontSize: 20,
              },
            }}
            activeDeleteButton={variant !== 'signup'}
            activeRotation={false}
          />
        </Box>
        <Box
          sx={{
            display: isMobile ? 'flex' : 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
            gap: `${gap}px`,
            gridTemplateRows: '1fr 1fr',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            flexDirection: 'column',
          }}
        >
          {Array.from({ length: isMobile ? 2 : 6 }).map((_, index) => (
            <FileDropZone
              overrideIllustration={
                color === 'primary' ? Images.FileDropZone.Owner : Images.FileDropZone.OwnerPetsitter
              }
              key={index}
              variant={images.findIndex((v) => v === null) === index ? 'default' : 'small'}
              isOwner={true}
              maxFiles={1}
              multipleFiles={false}
              overrideDropZoneBox={{
                width: isMobile ? 'calc(100% - 10px)' : '100%',
                height: isMobile ? 'auto' : '100%',
                aspectRatio: 1,
                border: (theme: any) =>
                  images[index] === currentImageView && images[index] !== null
                    ? `2px solid ${(theme.palette as any).purple.dark}`
                    : null,
                borderColor: '#BBBABF',
                transition: 'none',
                '& .emt-icons': {
                  top: 'unset',
                  color: (theme: any) => (theme.palette as any).purple.dark,
                  backgroundColor: '#fff',
                  fontSize: 16,
                  zIndex: 1,
                },
                alignSelf: 'end',
              }}
              activeDeleteButton={variant !== 'signup'}
              activeRotation={false}
              preview={images[index] as any}
              onDrop={handleDrop(index)}
            />
          ))}
        </Box>
      </Box>
      {errors.mainPicture && (
        <FormHelperText sx={{ marginTop: '20px', fontFamily: 'Nunito Sans' }} error>
          {errors.mainPicture}
        </FormHelperText>
      )}
    </Box>
  )
}

export default ImageUploader
